
import { cn } from '@/lib/utils';

type ChangelogBadgeProps = {
  children: React.ReactNode;
  tone?: 'live' | 'draft' | 'scheduled' | 'muted' | 'vip';
};

const toneCls: Record<NonNullable<ChangelogBadgeProps['tone']>, string> = {
  live: 'border-emerald-400/35 bg-emerald-500/12 text-emerald-100 shadow-[0_0_24px_-8px_rgba(52,211,153,0.35)]',
  draft: 'border-amber-400/30 bg-amber-500/12 text-amber-100 shadow-[0_0_24px_-8px_rgba(251,191,36,0.2)]',
  scheduled: 'border-sky-400/35 bg-sky-500/12 text-sky-100 shadow-[0_0_24px_-8px_rgba(56,189,248,0.25)]',
  muted: 'border-white/10 bg-white/[0.05] text-blue-100/85',
  vip: 'border-fuchsia-400/35 bg-fuchsia-500/12 text-fuchsia-100 shadow-[0_0_26px_-8px_rgba(217,70,239,0.35)]',
};

export function ChangelogBadge({ children, tone = 'muted' }: ChangelogBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-wider',
        toneCls[tone]
      )}
    >
      {children}
    </span>
  );
}

type ChangelogPageShellProps = {
  children: React.ReactNode;
  className?: string;
};

export function ChangelogPageShell({ children, className }: ChangelogPageShellProps) {
  return (
    <div className={cn('relative isolate min-h-[calc(100vh-8rem)]', className)}>
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.35]"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 80% 50% at 50% -20%, rgba(56,189,248,0.22), transparent 55%),
            radial-gradient(circle at 90% 20%, rgba(99,102,241,0.12), transparent 40%),
            linear-gradient(to bottom, transparent, rgba(3,0,35,0.6))
          `,
        }}
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.08]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(148,163,184,0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.2) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
        aria-hidden
      />
      {children}
    </div>
  );
}
